import { useContext, useEffect, useState } from "react";
import { updateUserDetails } from "../../api/authApi";
import ReactNotifications, { showApiErrors } from "../common/reactNotifications/ReactNotifications";
import * as utilConstants from "../../utils/constants/utilsConstants";
import Context from "../../context/Context";
import ChangePassword from "./ChangePassword";
import IsValidUserProfileData from "../ValidationChecker/profileValidation";
import { updateHierarchy } from "../../api/companyStructureApi/companyStructureApi";
import DeactivateAccount from "./DeactivateAccount";
import { FaLock, FaTrash } from 'react-icons/fa';

const UserProfile = () => {
    const { user, setUser, refreshUser } = useContext(Context);
    const [personalValues, setPersonalValues] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
    });
    const [companyValues, setCompanyValues] = useState({
        company_name: "",
        industry_focus: "",
        country_region: "",
        state: "",
        address1: "",
        address2: "",
        pin_code: "",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [removeProfileImage, setRemoveProfileImage] = useState(false);

    useEffect(() => {
        return () => {
            setRemoveProfileImage(false);
        };
    }, []);

    const getInitialPersonalValues = () => ({
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        phone_number: user?.phone_number || "",
    });

    const getInitialCompanyValues = () => ({
        company_name: user?.company?.company_name || "",
        industry_focus: user?.company?.industry_focus || "",
        country_region: user?.company?.country_region || "",
        state: user?.company?.state || "",
        address1: user?.company?.address1 || "",
        address2: user?.company?.address2 || "",
        pin_code: user?.company?.pin_code || "",
    });

    const arePersonalDetailsChanged = () => {
        const initial = getInitialPersonalValues();
        for (const key in personalValues) {
            const currentVal = personalValues[key];
            const initialVal = initial[key];
            if (typeof currentVal === 'string' && typeof initialVal === 'string') {
                if (currentVal.trim() !== initialVal.trim()) return true;
            } else if (currentVal !== initialVal) {
                return true;
            }
        }
        return false;
    };

    const areCompanyDetailsChanged = () => {
        const initial = getInitialCompanyValues();
        for (const key in companyValues) {
            const currentVal = companyValues[key];
            const initialVal = initial[key];
            if (typeof currentVal === 'string' && typeof initialVal === 'string') {
                if (currentVal.trim() !== initialVal.trim()) return true;
            } else if (currentVal !== initialVal) {
                return true;
            }
        }
        return false;
    };

    const isCompanyHead = user?.role === "Company Head";
    const showCompanyDetails = isCompanyHead;
    const isCompany = ['Company Head', 'Team Head', 'Member', 'Company Member'].includes(user?.role)
    const { setPageTitle } = useContext(Context);

    useEffect(() => {
        setPageTitle("Profile Details")
        if (user) {
            setPersonalValues(getInitialPersonalValues());
            setCompanyValues(getInitialCompanyValues());
            setProfileImageFile(null);
            if (user.profile_image && !removeProfileImage) {
                setProfileImagePreview(user.profile_image);
            } else {
                setProfileImagePreview(null);
            }
        }
    }, [user, showCompanyDetails, setPageTitle, removeProfileImage]);

    useEffect(() => {
        const personalChanged = arePersonalDetailsChanged();
        const companyChanged = showCompanyDetails && areCompanyDetailsChanged();
        const profileImageChanged = profileImageFile !== null;
        setHasPendingChanges(personalChanged || companyChanged || profileImageChanged || removeProfileImage);
    }, [personalValues, companyValues, user, showCompanyDetails, isCompanyHead, profileImageFile, removeProfileImage]);


    const handlePersonalChange = (e) => {
        const { name, value } = e.target;
        setPersonalValues((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff', 'image/webp'];
        const maxSize = 10 * 1024 * 1024;

        if (!allowedTypes.includes(file.type)) {
            ReactNotifications(
                "Invalid file type. Please upload JPG, PNG, GIF, BMP, TIFF, or WebP.",
                utilConstants.getErrorStatus
            );
            e.target.value = "";
            return;
        }

        if (file.size > maxSize) {
            ReactNotifications(
                "File is too large. Maximum size is 10MB.",
                utilConstants.getErrorStatus
            );
            e.target.value = "";
            return;
        }

        setProfileImageFile(file);
        setProfileImagePreview(URL.createObjectURL(file));
    };

    const handleRemoveProfileImage = () => {
        setProfileImageFile(null);
        setProfileImagePreview(null);
        setRemoveProfileImage(true);
    };

    const handleCompanyChange = (e) => {
        const { name, value } = e.target;
        setCompanyValues((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setLoading(true);

        const personalChanged = arePersonalDetailsChanged();
        const companyChanged = showCompanyDetails && areCompanyDetailsChanged();
        const profileImageChanged = profileImageFile !== null;

        if (!personalChanged && !companyChanged && !profileImageChanged && !removeProfileImage) {
            ReactNotifications("No changes detected", utilConstants.getErrorStatus);
            setLoading(false);
            return;
        }

        const valuesToValidate = showCompanyDetails
            ? { ...personalValues, ...companyValues }
            : personalValues;

        const validationOptions = {
            validatePersonalDetails: true,
            validateCompanyDetails: showCompanyDetails,
            optionalFields: showCompanyDetails
                ? ['address2']
                : [],
            requiredFields: []
        };

        const { isValid, errors: validationErrors } = IsValidUserProfileData(
            valuesToValidate,
            validationOptions
        );

        if (isValid) {
            if (personalChanged || profileImageChanged || removeProfileImage) {
                const formData = new FormData();
                Object.keys(personalValues).forEach(key => {
                    formData.append(key, personalValues[key]);
});

                if (profileImageFile) {
                    formData.append('profile_image_file', profileImageFile);
                }

                if (removeProfileImage) {
                    formData.append('remove_profile_image', true);
                }

                updateUserDetails(user?.id, formData, (res) => {
                    setLoading(false);
                    setErrors({});
                    if (res?.status === true || res?.status === 200) {
                        ReactNotifications(
                            res?.message || res?.data?.message || "Profile updated successfully",
                            utilConstants.sucessState200
                        );
                        setUser(res?.data);
                        setProfileImageFile(null);
                        setRemoveProfileImage(false);
                    } else if (res?.data?.errors) {
                        showApiErrors(res);
                        const capitalizeFirstLetter = (message) => {
                            return message.charAt(0).toUpperCase() + message.slice(1);
                        };
                        const formattedErrors = Object.keys(res?.data?.errors).reduce((acc, key) => {
                            acc[key] = capitalizeFirstLetter(res?.data?.errors[key][0]);
                            return acc;
                        }, {});
                        setErrors(formattedErrors);
                        if (res?.data?.errors?.error) {
                            ReactNotifications(
                                res?.data?.errors?.error[0],
                                utilConstants.getErrorStatus
                            );
                        }
                    } else {
                        ReactNotifications("Something went wrong", utilConstants.getErrorStatus);
                    }
                });
            }

            if (companyChanged && showCompanyDetails) {
                updateHierarchy(user?.company_id, companyValues, (res) => {
                    setLoading(false);
                    setErrors({});
                    if (res?.status === true || res?.status === 200) {
                        ReactNotifications(
                            res?.message || res?.data?.message || "Company details updated successfully",
                            utilConstants.sucessState200
                        );
                        refreshUser();
                    } else if (res?.data?.errors) {
                        showApiErrors(res);

                        const capitalizeFirstLetter = (message) => {
                            return message.charAt(0).toUpperCase() + message.slice(1);
                        };
                        const formattedErrors = Object.keys(res?.data?.errors).reduce((acc, key) => {
                            acc[key] = capitalizeFirstLetter(res?.data?.errors[key][0]);
                            return acc;
                        }, {});
                        setErrors(formattedErrors);
                        if (res?.data?.errors?.error) {
                            ReactNotifications(
                                res?.data?.errors?.error[0],
                                utilConstants.getErrorStatus
                            );
                        }
                    } else {
                        ReactNotifications("Something went wrong", utilConstants.getErrorStatus);
                    }
                });
            }
        } else {
            setErrors(validationErrors);
            setLoading(false);
        }
    };

    return (
        <div className="w-full px-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-end gap-4 mb-10 mt-4">
                <div className="flex flex-col sm:flex-row w-full sm:w-auto justify-end gap-3">
                    <ChangePassword />
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        className="py-[10px] px-[16px] w-[158px] h-[42px] font-bold !rounded-[4px] bg-[#AEF468] text-black flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!hasPendingChanges || loading}
                    >
                        {loading ? "Saving..." : "Save changes"}
                    </button>
                </div>
            </div>
            <form onSubmit={handleSubmit} className={`grid grid-cols-1 ${isCompany ? 'lg:grid-cols-2' : ''} gap-6`}>

                <div className="bg-white p-8 rounded-xl border shadow-sm">
                    <h3 className="text-2xl font-semibold mb-4">Personal details</h3>
                    <div className="space-y-4">
                        <div className="flex items-center mb-6">
                            <label htmlFor="profile_image_file" className="relative cursor-pointer group">
                                {profileImagePreview ? (
                                    <div className="relative w-[147.31px] h-[147.31px] rounded-full overflow-hidden">
                                        <img
                                            src={profileImagePreview}
                                            alt="Profile"
                                            className="w-full h-full object-cover transition-opacity group-hover:opacity-30"
                                            onError={(e) => {
                                                console.error("Image failed to load:", profileImagePreview);
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300">
                                            <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity text-center px-4">
                                                Click to update picture
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative w-[147.31px] h-[147.31px] rounded-full bg-[#7E8084] flex items-center justify-center text-white text-4xl font-bold group-hover:bg-opacity-80 transition-all">
                                        {personalValues.first_name?.[0]?.toUpperCase()}{personalValues.last_name?.[0]?.toUpperCase()}
                                        <div className="absolute inset-0 flex items-center justify-center bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 rounded-full">
                                            <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity text-center px-4">
                                                Click to upload picture
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <input
                                    id="profile_image_file"
                                    name="profile_image_file"
                                    type="file"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    accept="image/*"
                                />
                            </label>
                            <div className="ml-6">
                                <p className="text-sm text-gray-600 font-medium mb-1">Update Profile Photo</p>
                                <p className="text-xs text-gray-500">JPG, PNG, GIF, BMP, TIFF, WebP, JFIF up to 10MB</p>
                                {profileImagePreview && (
                                    <button
                                        type="button"
                                        onClick={handleRemoveProfileImage}
                                        className="text-red-500 text-xs font-medium flex items-center gap-1 mt-2"
                                    >
                                        <FaTrash />
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="w-1/2">
                                <input
                                    type="text"
                                    name="first_name"
                                    value={personalValues.first_name}
                                    onChange={handlePersonalChange}
                                    placeholder="First name"
                                    className={`w-full border rounded-lg px-3 py-2 ${errors.first_name ? 'border-red-500' : ''}`}
                                />
                                {errors.first_name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
                                )}
                            </div>
                            <div className="w-1/2">
                                <input
                                    type="text"
                                    name="last_name"
                                    value={personalValues.last_name}
                                    onChange={handlePersonalChange}
                                    placeholder="Last name"
                                    className={`w-full border rounded-lg px-3 py-2 ${errors.last_name ? 'border-red-500' : ''}`}
                                />
                                {errors.last_name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>
                                )}
                            </div>
                        </div>
                        <div className="relative">
                            <input
                                type="email"
                                name="email"
                                value={personalValues.email}
                                disabled
                                className="w-full border rounded-lg pl-3 pr-10 py-2 bg-gray-100 cursor-not-allowed text-gray-500"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <i className="bx bxs-lock-alt text-gray-400 text-lg"></i>
                            </div>

                            {errors.email && (
                                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                            )}
                        </div>
                        <div>
                            <input
                                type="text"
                                name="phone_number"
                                value={personalValues.phone_number}
                                onChange={handlePersonalChange}
                                placeholder="Phone number"
                                className={`w-full border rounded-lg px-3 py-2 ${errors.phone_number ? 'border-red-500' : ''}`}
                            />
                            {errors.phone_number && (
                                <p className="text-red-500 text-sm mt-1">{errors.phone_number}</p>
                            )}
                        </div>
                    </div>
                </div>

                {isCompany && <div className="bg-white p-8 rounded-xl border shadow-sm">
                    <h3 className="text-2xl font-semibold mb-4">Company details</h3>
                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <div className="w-1/2">
                                <input
                                    type="text"
                                    name="company_name"
                                    value={companyValues.company_name}
                                    onChange={handleCompanyChange}
                                    placeholder="Company name"
                                    className={`w-full border rounded-lg px-3 py-2 ${errors.company_name ? 'border-red-500' : ''}`}
                                    disabled={!showCompanyDetails}
                                />
                                {errors.company_name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.company_name}</p>
                                )}
                            </div>
                            <div className="w-1/2">
                                <input
                                    type="text"
                                    name="industry_focus"
                                    value={companyValues.industry_focus}
                                    onChange={handleCompanyChange}
                                    placeholder="Industry Focus"
                                    className={`w-full border rounded-lg px-3 py-2 ${errors.industry_focus ? 'border-red-500' : ''}`}
                                    disabled={!showCompanyDetails}
                                />
                                {errors.industry_focus && (
                                    <p className="text-red-500 text-sm mt-1">{errors.industry_focus}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="w-1/2">
                                <input
                                    type="text"
                                    name="country_region"
                                    value={companyValues.country_region}
                                    onChange={handleCompanyChange}
                                    placeholder="Country"
                                    className={`w-full border rounded-lg px-3 py-2 ${errors.country_region ? 'border-red-500' : ''}`}
                                    disabled={!showCompanyDetails}
                                />
                                {errors.country_region && (
                                    <p className="text-red-500 text-sm mt-1">{errors.country_region}</p>
                                )}
                            </div>
                            <div className="w-1/2">
                                <input
                                    type="text"
                                    name="state"
                                    value={companyValues.state}
                                    onChange={handleCompanyChange}
                                    placeholder="State"
                                    className={`w-full border rounded-lg px-3 py-2 ${errors.state ? 'border-red-500' : ''}`}
                                    disabled={!showCompanyDetails}
                                />
                                {errors.state && (
                                    <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                                )}
                            </div>
                        </div>
                        <div>
                            <input
                                type="text"
                                name="address1"
                                value={companyValues.address1}
                                onChange={handleCompanyChange}
                                placeholder="Office address line 1"
                                className={`w-full border rounded-lg px-3 py-2 ${errors.address1 ? 'border-red-500' : ''}`}
                                disabled={!showCompanyDetails}
                            />
                            {errors.address1 && (
                                <p className="text-red-500 text-sm mt-1">{errors.address1}</p>
                            )}
                        </div>
                        <div>
                            <input
                                type="text"
                                name="address2"
                                value={companyValues.address2}
                                onChange={handleCompanyChange}
                                placeholder="Office address line 2"
                                className={`w-full border rounded-lg px-3 py-2 ${errors.address2 ? 'border-red-500' : ''}`}
                                disabled={!showCompanyDetails}
                            />
                            {errors.address2 && (
                                <p className="text-red-500 text-sm mt-1">{errors.address2}</p>
                            )}
                        </div>
                        <div>
                            <input
                                type="text"
                                name="pin_code"
                                value={companyValues.pin_code}
                                onChange={handleCompanyChange}
                                placeholder="Zipcode"
                                className={`w-full border rounded-lg px-3 py-2 ${errors.pin_code ? 'border-red-500' : ''}`}
                                disabled={!showCompanyDetails}
                            />
                            {errors.pin_code && (
                                <p className="text-red-500 text-sm mt-1">{errors.pin_code}</p>
                            )}
                        </div>
                    </div>
                </div>}

            </form>
            <DeactivateAccount />
        </div>
    );
};

export default UserProfile;